// Frontend Web Push subscription helper.
//
// Lifecycle:
//   1. Caller invokes enablePush() - we register the SW (if not already),
//      ask for Notification permission, then ask the PushManager to subscribe
//      using the server's VAPID public key, then POST the subscription back
//      to the server so it can target this device.
//   2. disablePush() reverses step 1 and tells the server to forget this
//      endpoint.
//   3. getStatus() reports current capability + subscribed state so the UI
//      can render the right toggle.

import { API_BASE } from '../config/api';
import AuthService from './authService';

const SW_PATH = '/service-worker.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isStandalonePWA() {
  // iOS exposes navigator.standalone; everywhere else we read display-mode.
  if (typeof navigator !== 'undefined' && 'standalone' in navigator && navigator.standalone) return true;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(display-mode: standalone)').matches;
  }
  return false;
}

// iOS only delivers web push to homescreen-installed PWAs. Trying to subscribe
// from a regular Safari tab silently fails and frustrates users - so we gate
// the UI on this check.
function canActuallySubscribe() {
  if (!isSupported()) return false;
  if (isIOS() && !isStandalonePWA()) return false;
  return true;
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  // Reuse an existing registration if present so we don't bounce the SW.
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_PATH);
}

async function getStatus() {
  if (!isSupported()) {
    return {
      supported: false,
      canSubscribe: false,
      permission: 'default',
      subscribed: false,
      reason: isIOS() ? 'ios_requires_homescreen_install' : 'browser_unsupported'
    };
  }
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    canSubscribe: canActuallySubscribe(),
    permission: Notification.permission,
    subscribed: !!sub,
    reason: !canActuallySubscribe()
      ? (isIOS() ? 'ios_requires_homescreen_install' : 'browser_unsupported')
      : null
  };
}

async function fetchPublicKey() {
  // Public endpoint - no auth needed, so don't run through handleResponse
  // (which throws on 403, but a fresh deploy without VAPID keys returns 503).
  const res = await fetch(`${API_BASE}/api/push/public-key`);
  if (!res.ok) throw new Error('Failed to fetch VAPID public key');
  const data = await res.json();
  if (!data.publicKey) throw new Error('Server did not return a public key');
  return data.publicKey;
}

async function enablePush() {
  if (!canActuallySubscribe()) {
    return {
      success: false,
      error: isIOS()
        ? 'On iOS, push notifications require adding Live Your Fun to your homescreen first.'
        : 'Push notifications are not supported in this browser.'
    };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { success: false, error: 'Notification permission denied' };
  }

  const reg = await registerServiceWorker();
  if (!reg) return { success: false, error: 'Service worker registration failed' };

  let publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!publicKey) {
    try {
      publicKey = await fetchPublicKey();
    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }
  } catch (err) {
    return { success: false, error: err.message || 'Subscription failed' };
  }

  try {
    const res = await fetch(`${API_BASE}/api/push/subscribe`, {
      method: 'POST',
      headers: AuthService.getJsonHeaders(),
      body: JSON.stringify({
        subscription: sub.toJSON(),
        userAgent: navigator.userAgent
      })
    });
    // Use AuthService.handleResponse so 401s flow through the standard
    // re-auth path (it throws "Authentication required" which we surface
    // back to the caller as a normal error result).
    await AuthService.handleResponse(res);
  } catch (err) {
    return { success: false, error: err.message || 'Failed to register subscription with server' };
  }

  return { success: true };
}

// `tellServer: false` drops only the browser subscription — for a session
// that has already expired, when there is no token to send (sign-out,
// BH-06-01). The push service then answers 404/410 for the endpoint and
// sendToUser revokes the server row on the next send.
async function disablePush({ tellServer = true } = {}) {
  if (!isSupported()) return { success: true };
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return { success: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { success: true };

  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) {
    // ignore - we still tell the server to forget us
  }

  if (!tellServer) return { success: true };
  try {
    const res = await fetch(`${API_BASE}/api/push/unsubscribe`, {
      method: 'POST',
      headers: AuthService.getJsonHeaders(),
      body: JSON.stringify({ endpoint })
    });
    await AuthService.handleResponse(res);
  } catch (e) {
    // best effort - we already unsubscribed locally
  }
  return { success: true };
}

async function sendTestPush() {
  try {
    const res = await fetch(`${API_BASE}/api/push/test`, {
      method: 'POST',
      headers: AuthService.getJsonHeaders()
    });
    const data = await AuthService.handleResponse(res);
    return { success: true, ...data };
  } catch (err) {
    return { success: false, error: err.message || 'Test failed' };
  }
}

const PushSubscriptionService = {
  isSupported,
  canActuallySubscribe,
  isIOS,
  isStandalonePWA,
  getStatus,
  enablePush,
  disablePush,
  sendTestPush,
  registerServiceWorker
};

export default PushSubscriptionService;
